/**
 * Lookup data and pure predicates for The Password Game, kept separate from the
 * rule list so both stay readable.
 */

export const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december'
];

export const SPONSORS = ['factiii', 'tron', 'truth'];

/** Only uppercase counts, which is what makes the letter and numeral rules fight. */
export const ROMAN_VALUES: Record<string, number> = {
  I: 1,
  V: 5,
  X: 10,
  L: 50,
  C: 100,
  D: 500,
  M: 1000
};

export const ROMAN_PRODUCT_TARGET = 50;
export const DIGIT_SUM_TARGET = 100;
export const ATOMIC_SUM_TARGET = 40;

/**
 * Enough of the periodic table to make the atomic-sum rule solvable several
 * ways (Ca+Ca, Zn+Ne, Ar+Ar, Cl+K…). Two-character symbols are matched before
 * one-character ones so "Ca" never reads as C followed by a stray 'a'.
 */
export const ATOMIC_NUMBERS: Record<string, number> = {
  H: 1,
  He: 2,
  Li: 3,
  Be: 4,
  B: 5,
  C: 6,
  N: 7,
  O: 8,
  F: 9,
  Ne: 10,
  Na: 11,
  Mg: 12,
  Al: 13,
  Si: 14,
  P: 15,
  S: 16,
  Cl: 17,
  Ar: 18,
  K: 19,
  Ca: 20,
  Sc: 21,
  Ti: 22,
  V: 23,
  Cr: 24,
  Mn: 25,
  Fe: 26,
  Co: 27,
  Ni: 28,
  Cu: 29,
  Zn: 30,
  Ga: 31,
  Ge: 32,
  As: 33,
  Se: 34,
  Br: 35,
  Kr: 36
};

/** The eight moon-phase emoji, new moon first. */
export const MOON_PHASES = ['🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘'];

export const PET = '🐣';
export const WORM = '🐛';
export const WORMS_REQUIRED = 3;

/** Letters offered for sacrifice — vowels excluded so the game stays winnable. */
export const SACRIFICE_POOL = ['b', 'g', 'j', 'k', 'q', 'w', 'x', 'z'] as const;
export const SACRIFICE_COUNT = 2;

/** CAPTCHA codes are letters only: digits would fight the digit-sum rule. */
const CAPTCHA_ALPHABET = 'acdefhmnprstuvy';
export const CAPTCHA_LENGTH = 5;

export const makeCaptcha = (random: () => number = Math.random) =>
  Array.from(
    { length: CAPTCHA_LENGTH },
    () => CAPTCHA_ALPHABET[Math.floor(random() * CAPTCHA_ALPHABET.length)]
  ).join('');

export const digitSum = (value: string) =>
  [...value]
    .filter((char) => char >= '0' && char <= '9')
    .reduce((sum, digit) => sum + Number(digit), 0);

export const romanProduct = (value: string) => {
  const numerals = [...value].filter((char) => char in ROMAN_VALUES);
  if (numerals.length === 0) return 0;

  return numerals.reduce((product, char) => product * ROMAN_VALUES[char], 1);
};

export const isPrime = (n: number) => {
  if (n < 2) return false;
  for (let divisor = 2; divisor * divisor <= n; divisor += 1) {
    if (n % divisor === 0) return false;
  }
  return true;
};

/** Expand around every centre — cheap enough to run on each keystroke. */
export const hasPalindrome = (value: string, minLength = 3) => {
  const lower = value.toLowerCase();

  for (let centre = 0; centre < lower.length; centre += 1) {
    for (const offset of [0, 1]) {
      let left = centre;
      let right = centre + offset;

      while (
        left >= 0 &&
        right < lower.length &&
        lower[left] === lower[right]
      ) {
        if (right - left + 1 >= minLength) return true;
        left -= 1;
        right += 1;
      }
    }
  }

  return false;
};

/** Every chemical symbol appearing in the text, longest-match-first. */
export const atomicSymbolsIn = (value: string) => {
  const found: number[] = [];

  for (let i = 0; i < value.length; i += 1) {
    const two = value.slice(i, i + 2);
    if (two.length === 2 && two in ATOMIC_NUMBERS) {
      found.push(ATOMIC_NUMBERS[two]);
      i += 1;
      continue;
    }

    const one = value[i];
    if (one in ATOMIC_NUMBERS) found.push(ATOMIC_NUMBERS[one]);
  }

  return found;
};

/** True when some pair of symbols (which may be the same symbol twice) hits the target. */
export const hasAtomicPairSumming = (value: string, target: number) => {
  const numbers = atomicSymbolsIn(value);

  for (let i = 0; i < numbers.length; i += 1) {
    for (let j = i + 1; j < numbers.length; j += 1) {
      if (numbers[i] + numbers[j] === target) return true;
    }
  }

  return false;
};

/**
 * Moon phase from a date, via the days elapsed since a known new moon
 * (2000-01-06) divided into the synodic month. Accurate to within a day, which
 * is all a password rule needs.
 */
export const moonPhaseFor = (date: Date) => {
  const SYNODIC_DAYS = 29.530588853;
  const KNOWN_NEW_MOON = Date.UTC(2000, 0, 6, 18, 14);
  const days = (date.getTime() - KNOWN_NEW_MOON) / 86_400_000;
  const age = ((days % SYNODIC_DAYS) + SYNODIC_DAYS) % SYNODIC_DAYS;
  const index = Math.floor((age / SYNODIC_DAYS) * MOON_PHASES.length);

  return MOON_PHASES[index % MOON_PHASES.length];
};

export const countOf = (value: string, needle: string) =>
  [...value.matchAll(new RegExp(needle, 'gu'))].length;
